import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import useSWR from "swr";
import { useMemo } from "react";
import type { ProductSummary } from "@/common/productSummary/types";
import type {
  UmbrellaPromoPageVariant,
  UmbrellaPromoPageTestResult,
  UseUmbrellaPromoPage,
  BannerGroup,
  HeroData,
  PromoSectionOrderItem,
  AccordionData,
  ProductListDataRaw,
} from "./types";

const CUSTOM_RULE_ID = "umbrellaPromoPage";

const fetchProductSummaries = async (
  url: string,
  uuids: string[],
): Promise<ProductSummary[]> => {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });
  if (!res.ok) throw new Error("Failed to fetch product summaries");
  return res.json();
};

/**
 * Hook for the umbrella promo page experiment.
 * This experiment controls the PromoBanner variant slimTagged on the /offers page
 * and the Hero component with hero data (header, subheader, offers).
 *
 * The hook fetches product summaries from Monarch UUIDs and hydrates the productLists.
 */
const useUmbrellaPromoPage: UseUmbrellaPromoPage = () => {
  // Call the Monarch API to get the experiment result (raw data with UUIDs only)
  const { isLoading: isMonarchLoading, value } = useMonarchRule<{
    data: {
      banners?: BannerGroup[];
      hero?: HeroData;
      promoSectionOrder?: PromoSectionOrderItem[];
      faq?: AccordionData;
      productLists?: ProductListDataRaw[]; // Raw data from Monarch (only UUIDs)
    };
    variantCustomId: UmbrellaPromoPageVariant;
  }>(CUSTOM_RULE_ID, {});

  // Collect all UUIDs from all product lists
  const productUuids = useMemo(() => {
    const uuids: string[] = [];
    value?.data?.productLists?.forEach((productList) => {
      if (productList.uuids) {
        uuids.push(...productList.uuids);
      }
    });
    return uuids;
  }, [value?.data?.productLists]);

  // Fetch product summaries for all UUIDs using useSWR
  const { data: productSummaries, isLoading: isProductSummariesLoading } =
    useSWR(
      productUuids.length > 0 ? ["/api/product-summaries", productUuids] : null,
      ([url, uuids]) => fetchProductSummaries(url, uuids),
    );

  // Transform productLists to include full product summaries
  // This hydrates the raw Monarch data (UUIDs only) with actual ProductSummary objects
  // Memoized to avoid recalculating on every render
  const transformedProductLists = useMemo(() => {
    if (!value?.data?.productLists) {
      return undefined;
    }

    return value.data.productLists.map((productList) => {
      if (!productSummaries || !productList.uuids) {
        return productList;
      }

      // Create a map of product summaries by UUID for quick lookup
      const productSummariesByUuid = new Map(
        productSummaries.map((summary) => [summary.productUuid, summary]),
      );

      // Map UUIDs to full product summaries in order
      const products = productList.uuids
        .map((uuid) => productSummariesByUuid.get(uuid))
        .filter((product): product is ProductSummary => product !== undefined);

      return {
        ...productList,
        products, // Add the hydrated products array
      };
    });
  }, [value?.data?.productLists, productSummaries]);

  const isLoading = isMonarchLoading || isProductSummariesLoading;

  // If still loading or no value returned, return loading state
  if (isLoading || !value) {
    return {
      isLoading,
    } as UmbrellaPromoPageTestResult;
  }

  // Return the experiment result with proper typing
  const bannersArray: BannerGroup[] = Array.isArray(value.data?.banners)
    ? value.data.banners
    : [];

  const promoSectionOrder: PromoSectionOrderItem[] = Array.isArray(
    value.data?.promoSectionOrder,
  )
    ? value.data.promoSectionOrder
    : [];

  return {
    isLoading: false,
    variant: value.variantCustomId as UmbrellaPromoPageVariant,
    data: {
      banners: bannersArray.length > 0 ? bannersArray : undefined,
      hero: value.data?.hero,
      promoSectionOrder:
        promoSectionOrder.length > 0 ? promoSectionOrder : undefined,
      faq: value.data?.faq,
      productLists: transformedProductLists, // Now includes hydrated products
    },
  };
};

export default useUmbrellaPromoPage;
